import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { DirectoryData } from '@ail/shared/directory'
import type { NamingRules } from '@ail/shared/naming'
import { api, onAuthProblem, setActiveProject } from './http'
import { setDirectory } from './domain'

/*
 * ログインしている人・見ている契約プロジェクト・その中での役割。
 * 役割：admin（管理者）・operator（運用者）・viewer（閲覧）・ops_view（運営が中を読むだけ）。
 */

export type Role = 'admin' | 'operator' | 'viewer' | 'ops_view'

export interface Me {
  user: { id: string; email: string; name: string; isOperator: boolean; totpEnabled: boolean; prefs: Record<string, unknown> } | null
  mfa?: 'verify' | 'setup' | 'ok'
  projects?: Array<{ id: string; name: string; role: Exclude<Role, 'ops_view'>; status: string }>
  activeProject?: { id: string; name: string; status: string; role: Role; timezone: string; features: string[] } | null
}

interface AuthState {
  me: Me | undefined
  loading: boolean
  refresh: () => Promise<void>
  switchProject: (id: string) => Promise<void>
  logout: () => Promise<void>
  /** いまの契約で書き込めるか（運用者以上）。 */
  canWrite: boolean
  isAdmin: boolean
  hasFeature: (f: string) => boolean
  directoryReady: boolean
}

const Ctx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const me = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => api.get<Me>('/auth/me', undefined, signal),
    staleTime: 5 * 60_000,
    retry: false,
  })
  const active = me.data?.activeProject ?? null
  const [directoryReady, setDirectoryReady] = useState(false)

  // 契約が決まったら、その名簿と命名の読み方を読む
  useEffect(() => {
    setActiveProject(active?.id ?? null)
    if (!active) {
      setDirectoryReady(true)
      return
    }
    setDirectoryReady(false)
    let alive = true
    api
      .get<DirectoryData & { naming: NamingRules }>('/admin/directory')
      .then((d) => {
        if (!alive) return
        setDirectory({ teams: d.teams, operators: d.operators }, d.naming)
        setDirectoryReady(true)
      })
      .catch(() => alive && setDirectoryReady(true))
    return () => {
      alive = false
    }
  }, [active?.id])

  // ログインが切れたら、ログインの画面へ
  useEffect(
    () =>
      onAuthProblem(() => {
        void qc.invalidateQueries({ queryKey: ['auth', 'me'] })
      }),
    [qc],
  )

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['auth', 'me'] })
  }, [qc])

  const switchProject = useCallback(
    async (id: string) => {
      await api.post('/auth/project', { projectId: id })
      setActiveProject(id)
      // 前の契約の数字を残さない
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' })
      await qc.invalidateQueries({ queryKey: ['auth', 'me'] })
    },
    [qc],
  )

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } finally {
      setActiveProject(null)
      qc.clear()
      window.location.assign('/app/login')
    }
  }, [qc])

  const value = useMemo<AuthState>(() => {
    const role = active?.role
    return {
      me: me.data,
      loading: me.isLoading,
      refresh,
      switchProject,
      logout,
      canWrite: role === 'admin' || role === 'operator',
      isAdmin: role === 'admin',
      hasFeature: (f: string) => Boolean(active?.features.includes(f)),
      directoryReady,
    }
  }, [me.data, me.isLoading, refresh, switchProject, logout, active, directoryReady])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthState {
  const v = useContext(Ctx)
  if (!v) throw new Error('AuthProvider の外で useAuth を呼んでいます')
  return v
}

export const ROLE_LABELS: Record<Role, string> = {
  admin: '管理者',
  operator: '運用者',
  viewer: '閲覧',
  ops_view: '運営（閲覧）',
}

export const FEATURE_LABELS: Record<string, string> = {
  auto_rules: '自動ルール',
  bulk_submit: '一括入稿',
  reports_plus: '詳しいレポート',
  creative_ai: '制作のAI',
  mcp: 'MCP',
}
